import { Loader2, Mic, Square } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { Modal } from "~/components/ui/modal";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

type RecordingState =
  | { status: "idle" }
  | { status: "recording"; startTime: number }
  | { status: "uploading" }
  | { status: "error"; message: string };

interface VoiceRecorderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  caseId: string;
}

export const VoiceRecorderModal: FC<VoiceRecorderModalProps> = ({
  open,
  onOpenChange,
  organizationId,
  caseId,
}) => {
  const intl = useIntl();
  const [recordingState, setRecordingState] = useState<RecordingState>({
    status: "idle",
  });
  const [elapsedTime, setElapsedTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const cleanupRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    audioChunksRef.current = [];
  }, []);

  useEffect(() => {
    if (!open) {
      cleanupRecording();
      setRecordingState({ status: "idle" });
      setElapsedTime(0);
    }
  }, [open, cleanupRecording]);

  useEffect(() => {
    if (open) {
      trackEvent({
        eventName: "app/case/voice_recorder.modal/open",
        properties: { caseId, organizationId },
      });
    }
  }, [open, caseId, organizationId]);

  const startRecording = async () => {
    try {
      trackEvent({
        eventName: "app/case/voice_recorder.record_button/click",
        properties: { caseId, organizationId },
      });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);

      const startTime = Date.now();
      setRecordingState({ status: "recording", startTime });

      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } catch (error) {
      logger.error("Failed to start recording:", error);
      setRecordingState({
        status: "error",
        message: intl.formatMessage({
          id: "voiceRecorder.error.microphoneAccess",
          defaultMessage:
            "Could not access microphone. Please check your permissions.",
        }),
      });
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    trackEvent({
      eventName: "app/case/voice_recorder.stop_button/click",
      properties: {
        caseId,
        organizationId,
        recordingDurationSeconds: elapsedTime,
      },
    });

    setRecordingState({ status: "uploading" });

    return new Promise<void>((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve();
        return;
      }

      mediaRecorderRef.current.onstop = async () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        try {
          await uploadRecording(audioBlob);
          resolve();
        } catch (error) {
          logger.error("Failed to upload recording:", error);
          setRecordingState({
            status: "error",
            message: intl.formatMessage({
              id: "voiceRecorder.error.uploadFailed",
              defaultMessage: "Failed to upload recording. Please try again.",
            }),
          });
          resolve();
        }
      };

      mediaRecorderRef.current.stop();

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const uploadRecording = async (_audioBlob: Blob) => {
    console.error("not implemented yet");
    setRecordingState({
      status: "error",
      message: intl.formatMessage({
        id: "voiceRecorder.error.uploadFailed",
        defaultMessage: "Failed to upload recording. Please try again.",
      }),
    });
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getModalTitle = () => {
    if (recordingState.status === "uploading") {
      return intl.formatMessage({
        id: "voiceRecorder.title.uploading",
        defaultMessage: "Uploading...",
      });
    }
    return intl.formatMessage({
      id: "voiceRecorder.title.default",
      defaultMessage: "Record Voice Note",
    });
  };

  const getModalDescription = () => {
    if (recordingState.status === "recording") {
      return intl.formatMessage({
        id: "voiceRecorder.description.recording",
        defaultMessage: "Recording in progress. Click Stop when finished.",
      });
    }
    if (recordingState.status === "uploading") {
      return intl.formatMessage({
        id: "voiceRecorder.description.uploading",
        defaultMessage: "Please wait while your recording is being uploaded.",
      });
    }
    return intl.formatMessage({
      id: "voiceRecorder.description.default",
      defaultMessage:
        "Click the microphone button to start recording your clinical note.",
    });
  };

  const isRecording = recordingState.status === "recording";
  const isUploading = recordingState.status === "uploading";
  const hasError = recordingState.status === "error";

  return (
    <Modal
      open={open}
      onOpenChange={(newOpen) => {
        if (!isUploading) {
          onOpenChange(newOpen);
        }
      }}
      title={getModalTitle()}
      description={getModalDescription()}
      content={
        <div className="flex flex-col items-center justify-center py-8 gap-6">
          {isUploading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">
                <FormattedMessage
                  id="voiceRecorder.status.uploading"
                  defaultMessage="Uploading your recording..."
                />
              </p>
            </div>
          ) : (
            <>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isUploading}
                className={`rounded-full p-8 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isRecording
                    ? "bg-red-500 hover:bg-red-600 focus:ring-red-500 animate-pulse"
                    : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                }`}
                aria-label={
                  isRecording
                    ? intl.formatMessage({
                        id: "voiceRecorder.button.stop.ariaLabel",
                        defaultMessage: "Stop recording",
                      })
                    : intl.formatMessage({
                        id: "voiceRecorder.button.record.ariaLabel",
                        defaultMessage: "Start recording",
                      })
                }
              >
                {isRecording ? (
                  <Square className="w-8 h-8 text-white" fill="white" />
                ) : (
                  <Mic className="w-8 h-8 text-white" />
                )}
              </button>

              {isRecording && (
                <div className="text-center">
                  <p className="text-2xl font-mono font-semibold text-gray-900">
                    {formatTime(elapsedTime)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    <FormattedMessage
                      id="voiceRecorder.status.recording"
                      defaultMessage="Recording..."
                    />
                  </p>
                </div>
              )}

              {!isRecording && !hasError && (
                <p className="text-sm text-gray-500">
                  <FormattedMessage
                    id="voiceRecorder.hint.clickToRecord"
                    defaultMessage="Click to start recording"
                  />
                </p>
              )}

              {hasError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 max-w-sm">
                  <p className="text-red-800 text-sm text-center">
                    {recordingState.message}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      }
      footer={
        isUploading ? null : (
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUploading}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
        )
      }
    />
  );
};
